import React, { useEffect, useState } from "react";
import { 
  Table, Card, CardBody, CardHeader, Row, Col, Form, FormGroup, Label, Input,
  Button, ButtonGroup, Badge, Alert, Collapse
} from "reactstrap";
import { 
  getComprehensiveClosureSummary, 
  getDailyClosureSummary, 
  getWeeklyClosureSummary 
} from "../Payments/service/paymentService";
import ReactLoading from "react-loading";
import { overlayStyles, loaderContainerStyles } from "styles/commonStyles";
import { useSede } from "context/SedeContext";
import useSedeRefresh from "../../../hooks/useSedeRefresh";

function formatCurrency(q) {
  return `Q${parseFloat(q).toLocaleString("es-GT", { minimumFractionDigits: 2 })}`;
}

const monthNames = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"
];

const CierresSemanales = () => {
  const { addSedeParams, addSedeHeaders } = useSede();
  const [viewMode, setViewMode] = useState("comprehensive"); // "daily", "weekly", "comprehensive"
  const [filterType, setFilterType] = useState("month"); // "month", "year", "range", "date"
  const [meses, setMeses] = useState([]);
  const [years, setYears] = useState([]);
  const [mesSeleccionado, setMesSeleccionado] = useState("all");
  const [añoSeleccionado, setAñoSeleccionado] = useState(new Date().getFullYear());
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [fechaEspecifica, setFechaEspecifica] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});

  // Construir lista de meses y años
  useEffect(() => {
    const now = new Date();
    const listaMeses = ["all"];
    const listaAños = [];
    
    // Meses desde marzo hasta hoy
    const inicio = new Date(now.getFullYear(), 2, 1);
    while (inicio <= now) {
      const y = inicio.getFullYear();
      const m = inicio.getMonth() + 1;
      listaMeses.push(`${y}-${String(m).padStart(2,'0')}`);
      inicio.setMonth(inicio.getMonth() + 1);
    }
    
    // Años desde 2023 hasta hoy
    for (let year = 2023; year <= now.getFullYear(); year++) {
      listaAños.push(year);
    }
    
    setMeses(listaMeses);
    setYears(listaAños);
    setMesSeleccionado("all");
    setAñoSeleccionado(now.getFullYear());
  }, []);

  // Función para cargar datos
  const loadData = () => {
    if (!mesSeleccionado && !añoSeleccionado && !fechaInicio) return;
    
    setLoading(true);
    setError(null);
    
    let promise;
    let params = addSedeParams({});
    const headers = addSedeHeaders({});
    
    try {
      switch (filterType) {
        case "month":
          if (mesSeleccionado && mesSeleccionado !== "all") {
            params.month = mesSeleccionado;
          }
          break;
        case "year":
          params.year = añoSeleccionado;
          break;
        case "range":
          if (fechaInicio && fechaFin) {
            params.start_date = fechaInicio;
            params.end_date = fechaFin;
          } else {
            setError("Debe seleccionar fecha de inicio y fin");
            setLoading(false);
            return;
          }
          break;
        case "date":
          if (fechaEspecifica) {
            params.date = fechaEspecifica;
          } else {
            setError("Debe seleccionar una fecha específica");
            setLoading(false);
            return;
          }
          break;
        default:
          break;
      }
      
      switch (viewMode) {
        case "daily":
          promise = getDailyClosureSummary(params, params, headers);
          break;
        case "weekly":
          promise = getWeeklyClosureSummary(params, params, headers);
          break;
        case "comprehensive":
        default:
          promise = getComprehensiveClosureSummary(params, params, headers);
          break;
      }
      
      promise
        .then(response => {
          setData(response);
        })
        .catch(err => {
          console.error("Error loading data:", err);
          setError("Error al cargar los datos. Por favor, intente nuevamente.");
        })
      .finally(() => setLoading(false));
    } catch (err) {
      console.error("Error in loadData:", err);
      setError("Error interno. Por favor, intente nuevamente.");
      setLoading(false);
    }
  };

  // Escuchar cambios de sede y refrescar automáticamente
  useSedeRefresh(loadData, [addSedeParams, addSedeHeaders]);

  // Cargar datos según el modo y filtro seleccionado
  useEffect(() => {
    loadData();
  }, [viewMode, filterType, mesSeleccionado, añoSeleccionado, fechaInicio, fechaFin, fechaEspecifica]);

  // Función para manejar la expansión de días
  const toggleDayExpansion = (fecha) => {
    setExpandedDays(prev => ({
      ...prev,
      [fecha]: !prev[fecha]
    }));
  };

  if (loading) {
    return (
      <div style={overlayStyles}>
        <div style={loaderContainerStyles}>
          <ReactLoading type="spin" color="white" height="40%" />
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      {/* Header with view mode selection */}
      <Row className="mb-4">
        <Col md="12">
          <Card>
            <CardHeader>
              <h4 className="mb-0">Cierres Contables</h4>
              <p className="text-muted mb-0">Sistema mejorado con cierres diarios y semanales (Lunes-Sábado)</p>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="viewMode" style={{ fontWeight: 'bold' }}>Vista:</Label>
                    <ButtonGroup>
                      <Button 
                        color={viewMode === "comprehensive" ? "primary" : "outline-primary"}
                        onClick={() => setViewMode("comprehensive")}
                        size="sm"
                      >
                        Completa
                      </Button>
                      <Button 
                        color={viewMode === "daily" ? "primary" : "outline-primary"}
                        onClick={() => setViewMode("daily")}
                        size="sm"
                      >
                        Diaria
                      </Button>
                      <Button 
                        color={viewMode === "weekly" ? "primary" : "outline-primary"}
                        onClick={() => setViewMode("weekly")}
                        size="sm"
                      >
                        Semanal
                      </Button>
                    </ButtonGroup>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="filterType" style={{ fontWeight: 'bold' }}>Filtro:</Label>
                    <ButtonGroup>
                      <Button 
                        color={filterType === "month" ? "success" : "outline-success"}
                        onClick={() => setFilterType("month")}
                        size="sm"
                      >
                        Por Mes
                      </Button>
                      <Button 
                        color={filterType === "year" ? "success" : "outline-success"}
                        onClick={() => setFilterType("year")}
                        size="sm"
                      >
                        Por Año
                      </Button>
                      <Button 
                        color={filterType === "range" ? "success" : "outline-success"}
                        onClick={() => setFilterType("range")}
                        size="sm"
                      >
                        Por Rango
                      </Button>
                      <Button 
                        color={filterType === "date" ? "success" : "outline-success"}
                        onClick={() => setFilterType("date")}
                        size="sm"
                      >
                        Fecha Específica
                      </Button>
                    </ButtonGroup>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Filter controls */}
      <Row className="mb-3">
        <Col md="12">
          <Card>
            <CardBody>
              <Row>
                {filterType === "month" && (
        <Col md="4">
            <FormGroup>
              <Label for="mesFiltro" style={{ fontWeight: 'bold' }}>Mes:</Label>
              <Input
                type="select"
                id="mesFiltro"
                value={mesSeleccionado}
                onChange={e => setMesSeleccionado(e.target.value)}
              >
                        <option value="all">Todos los meses</option>
                {meses.filter(m => m !== 'all').map(m => {
                  const [y, mo] = m.split('-');
                  return (
                    <option key={m} value={m}>
                      {`${monthNames[parseInt(mo) - 1]} ${y}`}
                    </option>
                  );
                })}
              </Input>
            </FormGroup>
                  </Col>
                )}
                
                {filterType === "year" && (
                  <Col md="4">
                    <FormGroup>
                      <Label for="añoFiltro" style={{ fontWeight: 'bold' }}>Año:</Label>
                      <Input
                        type="select"
                        id="añoFiltro"
                        value={añoSeleccionado}
                        onChange={e => setAñoSeleccionado(parseInt(e.target.value))}
                      >
                        {years.map(year => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                )}
                
                {filterType === "range" && (
                  <>
                    <Col md="4">
                      <FormGroup>
                        <Label for="fechaInicio" style={{ fontWeight: 'bold' }}>Fecha Inicio:</Label>
                        <Input
                          type="date"
                          id="fechaInicio"
                          value={fechaInicio}
                          onChange={e => setFechaInicio(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="fechaFin" style={{ fontWeight: 'bold' }}>Fecha Fin:</Label>
                        <Input
                          type="date"
                          id="fechaFin"
                          value={fechaFin}
                          onChange={e => setFechaFin(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </>
                )}
                
                {filterType === "date" && (
                  <Col md="4">
                    <FormGroup>
                      <Label for="fechaEspecifica" style={{ fontWeight: 'bold' }}>Fecha Específica:</Label>
                      <Input
                        type="date"
                        id="fechaEspecifica"
                        value={fechaEspecifica}
                        onChange={e => setFechaEspecifica(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                )}
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Error display */}
      {error && (
        <Row className="mb-3">
          <Col md="12">
            <Alert color="danger">
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Data display based on view mode */}
      {data && (
        <>
          {/* Period summary */}
          <Row className="mb-3">
            <Col md="12">
              <Card>
                <CardHeader>
                  <strong>
                    📊 Resumen del Período: {data.periodo?.inicio && data.periodo?.fin 
                      ? `${new Date(data.periodo.inicio).toLocaleDateString()} - ${new Date(data.periodo.fin).toLocaleDateString()}`
                      : 'Período seleccionado'
                    }
                  </strong>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md="3">
                      <div className="text-center">
                        <h5 className="text-success">{formatCurrency(data.resumen_periodo?.total_efectivo || 0)}</h5>
                        <small className="text-muted">Efectivo</small>
                      </div>
                    </Col>
                    <Col md="3">
                      <div className="text-center">
                        <h5 className="text-info">{formatCurrency(data.resumen_periodo?.total_transferencia || 0)}</h5>
                        <small className="text-muted">Transferencia</small>
                      </div>
                    </Col>
                    <Col md="3">
                      <div className="text-center">
                        <h5 className="text-warning">{formatCurrency(data.resumen_periodo?.total_visalink || 0)}</h5>
                        <small className="text-muted">Visalink</small>
                      </div>
                    </Col>
                    <Col md="3">
                      <div className="text-center">
                        <h5 className="text-primary">{formatCurrency(data.resumen_periodo?.total_general || 0)}</h5>
                        <small className="text-muted">Total General</small>
                      </div>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md="4">
                      <Badge color="secondary" className="mr-2">
                        {data.resumen_periodo?.total_asistencias || 0} Asistencias
                      </Badge>
                    </Col>
                    <Col md="4">
                      <Badge color="info" className="mr-2">
                        {data.resumen_periodo?.total_paquetes || 0} Paquetes
                      </Badge>
                    </Col>
                    <Col md="4">
                      <Badge color="success" className="mr-2">
                        {data.resumen_periodo?.semanas_totales || data.resumen_periodo?.dias_totales || 0} {viewMode === 'weekly' ? 'Semanas' : 'Días'}
                      </Badge>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Daily view */}
          {viewMode === "daily" && data.cierres_diarios && (
            <Card className="mb-4">
              <CardHeader>
                <strong>📅 Cierres Diarios</strong>
              </CardHeader>
              <CardBody>
                <Table responsive bordered hover>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Día</th>
                      <th>Efectivo</th>
                      <th>Transferencia</th>
                      <th>Visalink</th>
                      <th>Total</th>
                      <th>Transacciones</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cierres_diarios.map((dia, i) => (
                      <React.Fragment key={i}>
                        <tr>
                          <td>{new Date(dia.fecha).toLocaleDateString('es-GT')}</td>
                          <td>{dia.dia_semana}</td>
                          <td>{formatCurrency(dia.efectivo)}</td>
                          <td>{formatCurrency(dia.transferencia)}</td>
                          <td>{formatCurrency(dia.visalink)}</td>
                          <td><strong>{formatCurrency(dia.total_dia)}</strong></td>
                          <td>
                            <Badge color="info">{dia.paquetes_vendidos}</Badge>
                          </td>
                          <td>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => toggleDayExpansion(dia.fecha)}
                              className="mr-2"
                            >
                              <i className="fa fa-eye"></i> Ver Detalle
                            </Button>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="8" style={{ padding: 0 }}>
                            <Collapse isOpen={expandedDays[dia.fecha]}>
                              <Card className="m-3">
                                <CardHeader>
                                  <strong>📋 DETALLE DEL DÍA - {new Date(dia.fecha).toLocaleDateString('es-GT')} ({dia.dia_semana})</strong>
                                </CardHeader>
                                <CardBody>
                                  {/* Daily summary */}
                                  <Row className="mb-3">
                                    <Col md="3">
                                      <div className="text-center">
                                        <h6 className="text-success">{formatCurrency(dia.efectivo)}</h6>
                                        <small>Efectivo</small>
                                      </div>
                                    </Col>
                                    <Col md="3">
                                      <div className="text-center">
                                        <h6 className="text-info">{formatCurrency(dia.transferencia)}</h6>
                                        <small>Transferencia</small>
                                      </div>
                                    </Col>
                                    <Col md="3">
                                      <div className="text-center">
                                        <h6 className="text-warning">{formatCurrency(dia.visalink)}</h6>
                                        <small>Visalink</small>
                                      </div>
                                    </Col>
                                    <Col md="3">
                                      <div className="text-center">
                                        <h6 className="text-primary">{formatCurrency(dia.total_dia)}</h6>
                                        <small>Total del Día</small>
                                      </div>
                                    </Col>
                                  </Row>

                                  {/* Individual transactions table */}
                                  {dia.detalle_pagos && dia.detalle_pagos.length > 0 && (
                                    <Table responsive bordered hover size="sm">
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Cliente</th>
                                          <th>Membresía</th>
                                          <th>Método de Pago</th>
                                          <th>Monto</th>
                                          <th>Observaciones</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {dia.detalle_pagos.map((pago, j) => (
                                          <tr key={j}>
                                            <td>{j + 1}</td>
                                            <td><strong>{pago.cliente}</strong></td>
                                            <td>{pago.membresia}</td>
                                            <td>
                                              <Badge 
                                                color={
                                                  pago.metodo.toLowerCase().includes('efectivo') || pago.metodo.toLowerCase().includes('cash') ? 'success' :
                                                  pago.metodo.toLowerCase().includes('transferencia') || pago.metodo.toLowerCase().includes('transfer') || pago.metodo.toLowerCase().includes('deposito') ? 'info' :
                                                  pago.metodo.toLowerCase().includes('visalink') || pago.metodo.toLowerCase().includes('visalink') || pago.metodo.toLowerCase().includes('card') ? 'warning' : 'secondary'
                                                }
                                              >
                                                {pago.metodo}
                                              </Badge>
                                            </td>
                                            <td><strong>{formatCurrency(pago.monto)}</strong></td>
                                            <td>{pago.observaciones}</td>
                                          </tr>
                                        ))}
                                        <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                          <td colSpan="4">TOTAL DEL DÍA</td>
                                          <td>{formatCurrency(dia.total_dia)}</td>
                                          <td>{dia.paquetes_vendidos} transacciones</td>
                                        </tr>
                                      </tbody>
                                    </Table>
                                  )}

                                  {/* Daily statistics */}
                                  <Row className="mt-3">
                                    <Col md="12">
                                      <Badge color="secondary" className="mr-2">{dia.asistencias} Asistencias</Badge>
                                      <Badge color="info" className="mr-2">{dia.paquetes_vendidos} Paquetes Vendidos</Badge>
                                      <Badge color="warning" className="mr-2">{dia.no_shows} No Shows</Badge>
                                      <Badge color="success" className="mr-2">{dia.clases_individuales} Clases Individuales</Badge>
                                    </Col>
                                  </Row>
                                </CardBody>
                              </Card>
                            </Collapse>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          )}

          {/* Weekly view */}
          {viewMode === "weekly" && data.cierres_semanales && (
            <Card className="mb-4">
              <CardHeader>
                <strong>📅 Cierres Semanales</strong>
              </CardHeader>
              <CardBody>
                <Table responsive bordered hover>
                  <thead>
                    <tr>
                      <th>Semana</th>
                      <th>Rango</th>
                      <th>Efectivo</th>
                      <th>Transferencia</th>
                      <th>Visalink</th>
                      <th>Total</th>
                      <th>Días</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cierres_semanales.map((semana, idx) => (
                      <React.Fragment key={idx}>
                        <tr>
                          <td><strong>Semana {semana.semana}</strong></td>
                          <td>{semana.rango}</td>
                          <td>{formatCurrency(semana.efectivo)}</td>
                          <td>{formatCurrency(semana.transferencia)}</td>
                          <td>{formatCurrency(semana.visalink)}</td>
                          <td><strong>{formatCurrency(semana.total_semana)}</strong></td>
                          <td>
                            <Badge color="info">{semana.dias_laborables}</Badge>
                          </td>
                          <td>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => toggleDayExpansion(`semana-${semana.semana}`)}
                              className="mr-2"
                            >
                              <i className="fa fa-eye"></i> Ver Detalle
                            </Button>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="8" style={{ padding: 0 }}>
                            <Collapse isOpen={expandedDays[`semana-${semana.semana}`]}>
                              <Card className="m-3">
                                <CardHeader>
                                  <strong>📋 DETALLE SEMANA {semana.semana} - {semana.rango}</strong>
                                </CardHeader>
                                <CardBody>
                                  <Row className="mb-3">
                                    <Col md="3">
                                      <div className="text-center">
                                        <h6 className="text-success">{formatCurrency(semana.efectivo)}</h6>
                                        <small>Efectivo</small>
                                      </div>
                                    </Col>
                                    <Col md="3">
                                      <div className="text-center">
                                        <h6 className="text-info">{formatCurrency(semana.transferencia)}</h6>
                                        <small>Transferencia</small>
                                      </div>
                                    </Col>
                                    <Col md="3">
                                      <div className="text-center">
                                        <h6 className="text-warning">{formatCurrency(semana.visalink)}</h6>
                                        <small>Visalink</small>
                                      </div>
                                    </Col>
                                    <Col md="3">
                                      <div className="text-center">
                                        <h6 className="text-primary">{formatCurrency(semana.total_semana)}</h6>
                                        <small>Total Semana</small>
                                      </div>
                                    </Col>
                                  </Row>
                                  {/* Individual transactions table */}
                                  {semana.detalle_pagos && semana.detalle_pagos.length > 0 && (
                                    <Table responsive bordered hover size="sm" className="mt-3">
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Cliente</th>
                                          <th>Membresía</th>
                                          <th>Fecha</th>
                                          <th>Método de Pago</th>
                                          <th>Monto</th>
                                          <th>Observaciones</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {semana.detalle_pagos.map((pago, j) => (
                                          <tr key={j}>
                                            <td>{j + 1}</td>
                                            <td><strong>{pago.cliente}</strong></td>
                                            <td>{pago.membresia}</td>
                                            <td>{new Date(pago.fecha).toLocaleDateString('es-GT')}</td>
                                            <td>
                                              <Badge 
                                                color={
                                                  pago.metodo.toLowerCase().includes('efectivo') || pago.metodo.toLowerCase().includes('cash') ? 'success' :
                                                  pago.metodo.toLowerCase().includes('transferencia') || pago.metodo.toLowerCase().includes('transfer') || pago.metodo.toLowerCase().includes('deposito') ? 'info' :
                                                  pago.metodo.toLowerCase().includes('visalink') || pago.metodo.toLowerCase().includes('visalink') || pago.metodo.toLowerCase().includes('card') ? 'warning' : 'secondary'
                                                }
                                              >
                                                {pago.metodo}
                                              </Badge>
                                            </td>
                                            <td><strong>{formatCurrency(pago.monto)}</strong></td>
                                            <td>{pago.observaciones}</td>
                                          </tr>
                                        ))}
                                        <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                          <td colSpan="5">TOTAL SEMANA {semana.semana}</td>
                                          <td>{formatCurrency(semana.total_semana)}</td>
                                          <td>{semana.paquetes_vendidos} transacciones</td>
                                        </tr>
                                      </tbody>
                                    </Table>
                                  )}

                                  <Row className="mt-3">
                                    <Col md="12">
                                      <Badge color="secondary" className="mr-2">{semana.asistencias} Asistencias</Badge>
                                      <Badge color="info" className="mr-2">{semana.paquetes_vendidos} Paquetes</Badge>
                                      <Badge color="warning" className="mr-2">{semana.no_shows} No Shows</Badge>
                                      <Badge color="success" className="mr-2">{semana.clases_individuales} Individuales</Badge>
                                    </Col>
                                  </Row>
                                </CardBody>
                              </Card>
                            </Collapse>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          )}

                    {/* Comprehensive view */}
          {viewMode === "comprehensive" && data.cierres_semanales && data.cierres_diarios && (
            <>
              {data.cierres_semanales.map((semana, idx) => {
                // Filter daily data for this week
                const weekDays = data.cierres_diarios.filter(dia => {
                  const diaDate = new Date(dia.fecha);
                  const startDate = new Date(semana.fecha_inicio);
                  const endDate = new Date(semana.fecha_fin);
                  return diaDate >= startDate && diaDate <= endDate;
                });

                return (
                  <Card className="mb-4" key={idx}>
                    <CardHeader>
                      <strong>📅 CIERRE SEMANA {semana.semana} - {semana.rango}</strong>
                      <Badge color="info" className="ml-2">{semana.dias_laborables} días laborables</Badge>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => toggleDayExpansion(`comprehensive-${semana.semana}`)}
                        className="ml-2"
                      >
                        <i className="fa fa-eye"></i> Ver Detalle Semana
                      </Button>
                    </CardHeader>
                    <CardBody>
                      {/* Week summary */}
                      <Row className="mb-3">
                        <Col md="3">
                          <div className="text-center">
                            <h6 className="text-success">{formatCurrency(semana.efectivo)}</h6>
                            <small>Efectivo</small>
                          </div>
                        </Col>
                        <Col md="3">
                          <div className="text-center">
                            <h6 className="text-info">{formatCurrency(semana.transferencia)}</h6>
                            <small>Transferencia</small>
                          </div>
                        </Col>
                        <Col md="3">
                          <div className="text-center">
                            <h6 className="text-warning">{formatCurrency(semana.visalink)}</h6>
                            <small>Visalink</small>
                          </div>
                        </Col>
                        <Col md="3">
                          <div className="text-center">
                            <h6 className="text-primary">{formatCurrency(semana.total_semana)}</h6>
                            <small>Total Semana</small>
                          </div>
                        </Col>
                      </Row>

                      {/* Daily breakdown */}
                      <Table responsive bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Fecha</th>
                            <th>Día</th>
                            <th>Efectivo</th>
                            <th>Transferencia</th>
                            <th>Visalink</th>
                            <th>Total</th>
                            <th>Asistencias</th>
                            <th>Paquetes</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {weekDays.map((dia, i) => (
                            <React.Fragment key={i}>
                              <tr>
                                <td>{new Date(dia.fecha).toLocaleDateString()}</td>
                                <td>{dia.dia_semana}</td>
                                <td>{formatCurrency(dia.efectivo)}</td>
                                <td>{formatCurrency(dia.transferencia)}</td>
                                <td>{formatCurrency(dia.visalink)}</td>
                                <td><strong>{formatCurrency(dia.total_dia)}</strong></td>
                                <td>{dia.asistencias}</td>
                                <td>{dia.paquetes_vendidos}</td>
                                <td>
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => toggleDayExpansion(dia.fecha)}
                                    className="mr-2"
                                  >
                                    <i className="fa fa-eye"></i> Ver Detalle
                                  </Button>
                                </td>
                              </tr>
                              <tr>
                                <td colSpan="9" style={{ padding: 0 }}>
                                  <Collapse isOpen={expandedDays[dia.fecha]}>
                                    <Card className="m-3">
                                      <CardHeader>
                                        <strong>📋 DETALLE DEL DÍA - {new Date(dia.fecha).toLocaleDateString('es-GT')} ({dia.dia_semana})</strong>
                                      </CardHeader>
                                      <CardBody>
                                        {/* Daily summary */}
                                        <Row className="mb-3">
                                          <Col md="3">
                                            <div className="text-center">
                                              <h6 className="text-success">{formatCurrency(dia.efectivo)}</h6>
                                              <small>Efectivo</small>
                                            </div>
                                          </Col>
                                          <Col md="3">
                                            <div className="text-center">
                                              <h6 className="text-info">{formatCurrency(dia.transferencia)}</h6>
                                              <small>Transferencia</small>
                                            </div>
                                          </Col>
                                          <Col md="3">
                                            <div className="text-center">
                                              <h6 className="text-warning">{formatCurrency(dia.visalink)}</h6>
                                              <small>Visalink</small>
                                            </div>
                                          </Col>
                                          <Col md="3">
                                            <div className="text-center">
                                              <h6 className="text-primary">{formatCurrency(dia.total_dia)}</h6>
                                              <small>Total del Día</small>
                                            </div>
                                          </Col>
                                        </Row>

                                        {/* Individual transactions table */}
                                        {dia.detalle_pagos && dia.detalle_pagos.length > 0 && (
                                          <Table responsive bordered hover size="sm">
                                            <thead>
                                              <tr>
                                                <th>#</th>
                                                <th>Cliente</th>
                                                <th>Membresía</th>
                                                <th>Método de Pago</th>
                                                <th>Monto</th>
                                                <th>Observaciones</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {dia.detalle_pagos.map((pago, j) => (
                                                <tr key={j}>
                                                  <td>{j + 1}</td>
                                                  <td><strong>{pago.cliente}</strong></td>
                                                  <td>{pago.membresia}</td>
                                                  <td>
                                                    <Badge 
                                                      color={
                                                        pago.metodo.toLowerCase().includes('efectivo') || pago.metodo.toLowerCase().includes('cash') ? 'success' :
                                                        pago.metodo.toLowerCase().includes('transferencia') || pago.metodo.toLowerCase().includes('transfer') || pago.metodo.toLowerCase().includes('deposito') ? 'info' :
                                                        pago.metodo.toLowerCase().includes('visalink') || pago.metodo.toLowerCase().includes('visalink') || pago.metodo.toLowerCase().includes('card') ? 'warning' : 'secondary'
                                                      }
                                                    >
                                                      {pago.metodo}
                                                    </Badge>
                                                  </td>
                                                  <td><strong>{formatCurrency(pago.monto)}</strong></td>
                                                  <td>{pago.observaciones}</td>
                                                </tr>
                                              ))}
                                              <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                <td colSpan="4">TOTAL DEL DÍA</td>
                                                <td>{formatCurrency(dia.total_dia)}</td>
                                                <td>{dia.paquetes_vendidos} transacciones</td>
                                              </tr>
                                            </tbody>
                                          </Table>
                                        )}

                                        {/* Daily statistics */}
                                        <Row className="mt-3">
                                          <Col md="12">
                                            <Badge color="secondary" className="mr-2">{dia.asistencias} Asistencias</Badge>
                                            <Badge color="info" className="mr-2">{dia.paquetes_vendidos} Paquetes Vendidos</Badge>
                                            <Badge color="warning" className="mr-2">{dia.no_shows} No Shows</Badge>
                                            <Badge color="success" className="mr-2">{dia.clases_individuales} Clases Individuales</Badge>
                                          </Col>
                                        </Row>
                                      </CardBody>
                                    </Card>
                                  </Collapse>
                                </td>
                              </tr>
                            </React.Fragment>
                          ))}
                          <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                            <td colSpan="2">TOTAL SEMANA {semana.semana}</td>
                            <td>{formatCurrency(semana.efectivo)}</td>
                            <td>{formatCurrency(semana.transferencia)}</td>
                            <td>{formatCurrency(semana.visalink)}</td>
                            <td>{formatCurrency(semana.total_semana)}</td>
                            <td>{semana.asistencias}</td>
                            <td>{semana.paquetes_vendidos}</td>
                            <td>-</td>
                          </tr>
                        </tbody>
                      </Table>

                      {/* Week detail expansion */}
                      <Collapse isOpen={expandedDays[`comprehensive-${semana.semana}`]}>
                        <Card className="mt-3">
                          <CardHeader>
                            <strong>📊 RESUMEN DETALLADO SEMANA {semana.semana}</strong>
                          </CardHeader>
                          <CardBody>
                            <Row>
                              <Col md="12">
                                <Badge color="secondary" className="mr-2">{semana.asistencias} Asistencias</Badge>
                                <Badge color="info" className="mr-2">{semana.paquetes_vendidos} Paquetes</Badge>
                                <Badge color="warning" className="mr-2">{semana.no_shows} No Shows</Badge>
                                <Badge color="success" className="mr-2">{semana.clases_individuales} Individuales</Badge>
                                <Badge color="primary" className="mr-2">{semana.dias_laborables} Días Laborables</Badge>
                              </Col>
                            </Row>
                          </CardBody>
                        </Card>
                      </Collapse>
                    </CardBody>
                  </Card>
                );
              })}
            </>
          )}
        </>
      )}

      {/* No data message */}
      {!loading && !data && !error && (
        <Row>
          <Col md="12">
      <Card>
              <CardBody className="text-center">
                <h5>No hay datos disponibles</h5>
                <p className="text-muted">Seleccione un período para ver los cierres contables.</p>
        </CardBody>
      </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default CierresSemanales;
